import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully. We will contact you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="contact-page">
      {/* Page Header */}
      <section className="px-6 sm:px-12 lg:px-24 py-16 sm:py-24">
        <motion.div
          className="max-w-4xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-6">
            Get in Touch
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6">
            Contact
          </h1>
          <p className="text-gray-400 text-base sm:text-lg font-light max-w-xl">
            We would be delighted to hear from you. Whether you have a question about our products 
            or wish to discuss a project, our team is here to help.
          </p>
        </motion.div>
      </section>

      {/* Contact Content */}
      <section className="px-6 sm:px-12 lg:px-24 pb-24 sm:pb-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-12">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                    Office
                  </p>
                  <p className="text-white font-light">
                    S. Pietro, 6854<br />
                    Tessin
                  </p>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                    Email
                  </p>
                  <a href="mailto:contact@introzzi.ch" className="text-white font-light hover:text-[#C5A059] transition-colors">
                    contact@introzzi.ch
                  </a>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                    Hours
                  </p>
                  <p className="text-gray-400 font-light">
                    Monday – Friday: 09:00 – 19:00
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white font-light focus:outline-none focus:border-white transition-colors"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white font-light focus:outline-none focus:border-white transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white font-light focus:outline-none focus:border-white transition-colors"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white font-light focus:outline-none focus:border-white transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 sm:px-12 lg:px-24 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <h3 className="font-['Playfair_Display'] text-2xl text-white mb-4">INTROZZI</h3>
              <p className="text-gray-500 text-sm font-light max-w-sm">
                Tailored bathroom fixtures and accessories. Crafted with precision, designed for silence.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Navigate</p>
              <ul className="space-y-3">
                <li><Link to="/collections" className="text-gray-400 hover:text-white transition-colors text-sm">Collections</Link></li>
                <li><Link to="/#our-story" className="text-gray-400 hover:text-white transition-colors text-sm">Our Story</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Contact</p>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>contact@introzzi.ch</li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs">&copy; 2022 INTROZZI. All rights reserved.</p>
            <p className="text-gray-700 text-[10px] mt-1">brand of idrotek &mdash; CHE-468.961.634</p>
            <div className="flex items-center gap-4">
              <span className="w-12 h-px bg-[#C5A059]" />
              <Link 
                to="/admin/login" 
                className="text-gray-700 hover:text-gray-500 transition-colors text-[10px]"
                data-testid="hidden-admin-link"
              >
                ●
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
